import type { ServiceMutationResult } from "@/shared/types/serviceResult";
import type { Watchlist } from "@/shared/types/watchlist";
import {
  watchlistsStore,
  type WatchlistSecuritiesFilterRequest,
  type WatchlistSecuritiesResponse,
  type WatchlistSecurityItem,
} from "@/stores/watchlists/watchlistsStore";

export type {
  WatchlistSecuritiesFilterRequest,
  WatchlistSecuritiesResponse,
  WatchlistSecurityItem,
};

// Thin facade over watchlistsStore — the store is the sole write-through cache
// for watchlist data (owns every @/apis/watchlistsApi call and the resulting
// state update in one place). This file exists only to give the rest of the
// app a stable "service" entry point; it must never call the API directly,
// so callers can't bypass the store and leave it stale.
export interface WatchlistsService {
  readonly domain: "watchlists";
  subscribe(listener: () => void): () => void;
  getVersion(): number;
  getState(): ReturnType<typeof watchlistsStore.getState>;
  getWatchlists(): Watchlist[];
  getWatchlistsForSecurityIdentifier(securityIdentifier: string): Watchlist[];
  reset(): void;
  ensureLoaded(): Promise<ServiceMutationResult<Watchlist[]>>;
  loadWatchlists(): Promise<ServiceMutationResult<Watchlist[]>>;
  loadWatchlistSecurities(
    watchlistId: string,
    filters?: WatchlistSecuritiesFilterRequest,
  ): Promise<ServiceMutationResult<WatchlistSecuritiesResponse>>;
  createWatchlist(name: string): Promise<ServiceMutationResult<Watchlist>>;
  renameWatchlist(id: string, name: string): Promise<ServiceMutationResult<Watchlist>>;
  deleteWatchlist(id: string): Promise<ServiceMutationResult<void>>;
  addSecurities(
    watchlistId: string,
    securityIdentifiers: string[],
  ): Promise<ServiceMutationResult<WatchlistSecurityItem[]>>;
  removeSecurities(
    watchlistId: string,
    securityIdentifiers: string[],
  ): Promise<ServiceMutationResult<WatchlistSecurityItem[]>>;
}

// watchlistsStore is a module-level singleton whose methods never read `this`,
// so forwarding them as unbound references keeps this facade a thin pass-through.
/* oxlint-disable typescript/unbound-method */
export const watchlistsService: WatchlistsService = {
  domain: "watchlists",
  subscribe: watchlistsStore.subscribe,
  getVersion: watchlistsStore.getVersion,
  getState: watchlistsStore.getState,
  getWatchlists: watchlistsStore.getWatchlists,
  getWatchlistsForSecurityIdentifier: watchlistsStore.getWatchlistsForSecurityIdentifier,
  reset: watchlistsStore.reset,
  ensureLoaded() {
    return watchlistsStore.ensureLoaded();
  },
  loadWatchlists: watchlistsStore.loadWatchlists,
  loadWatchlistSecurities: watchlistsStore.loadWatchlistSecurities,
  createWatchlist: watchlistsStore.createWatchlist,
  renameWatchlist: watchlistsStore.renameWatchlist,
  deleteWatchlist: watchlistsStore.deleteWatchlist,
  addSecurities: watchlistsStore.addSecurities,
  removeSecurities: watchlistsStore.removeSecurities,
};
/* oxlint-enable typescript/unbound-method */
